'use client';

import type { FC, ReactNode } from 'react';
import { Button } from '@/app/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/app/components/ui/dialog';

export type ConfirmDialogProps = {
  inheritTheme?: boolean;
  /** 需要覆盖任务中心等普通浮层时使用。 */
  aboveTaskCenter?: boolean;
  title?: string;
  description?: string;
  content?: ReactNode;
  primaryButtonText?: string;
  secondaryButtonText?: string;
  onPrimaryAction?: () => void;
  onSecondaryAction?: () => void;
  onClose?: () => void;
  closable?: boolean;
  /**
   * 视觉主题。'default' = 全站黑色风；'admission' = h5/thu-2026-freshmen 紫色风。
   * 在 GlobalModalManager 处按此字段切换实际渲染的组件，调用方仅需在 openConfirmDialog 时传入。
   */
  theme?: 'default' | 'admission';
};

/**
 * 确认对话框组件
 * 用于需要用户确认的操作场景
 */
export const ConfirmDialog: FC<ConfirmDialogProps> = ({
  inheritTheme = false,
  aboveTaskCenter = false,
  title,
  description,
  content,
  primaryButtonText,
  secondaryButtonText,
  onPrimaryAction,
  onSecondaryAction,
  onClose,
  closable = true,
}) => {
  return (
    <Dialog open={true} onOpenChange={(open) => !open && onClose?.()}>
      <DialogContent
        inheritTheme={inheritTheme}
        closable={closable}
        className={aboveTaskCenter ? 'z-[60]' : undefined}
        overlayClassname={aboveTaskCenter ? 'z-[60] opacity-30' : 'opacity-30'}
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <DialogHeader>
          {title && <DialogTitle className="text-system-text03 sm:text-xl">{title}</DialogTitle>}
          {description && <DialogDescription>{description}</DialogDescription>}
          {content && <div className="whitespace-pre-wrap pr-8 text-system-text03 sm:text-xs">{content}</div>}
        </DialogHeader>
        <DialogFooter className="mt-2">
          {secondaryButtonText && (
            <Button
              variant="outline"
              size="dialog"
              onClick={() => {
                onSecondaryAction?.();
                onClose?.();
              }}
            >
              {secondaryButtonText}
            </Button>
          )}
          {primaryButtonText && (
            <Button
              variant="primary"
              size="dialog"
              onClick={() => {
                onPrimaryAction?.();
                onClose?.();
              }}
            >
              {primaryButtonText}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
